// @flow

import { api } from './fetch';

export const getAccountInfo = () => api('getaccountinfo');

export const updateFamilyAccount = (id: string | number, name: string) =>
  api('updatefamilyaccount', {
    query: {
      familyaccount_id: id,
      familyaccount_name: name,
    },
  });

export const getParkerStatus = () => api('parkerstatus2');

export const logout = () => api('logout');

export type AuthFlow = 'LOGIN' | 'SIGNUP';

export const getVerificationCode = (email: string, authFlow: AuthFlow) =>
  api('requestverificationcode3', {
    query: {
      type: 'EMAIL',
      email: email,
      authflow: authFlow,
    },
  });

export type EmailVerificationResponse = {
  type: 200,
  data: {|
    haspin: boolean,
    verified: boolean,
    ppverifiedparker: string,
  |},
};

export const verifyEmail = ({
  email,
  code,
}: {|
  email: string,
  code: string,
|}) => api('verifyemail2', { query: { email, code } });

export type SaveParkerResponse =
  | $Exact<{
      type: 200,
      data: {|
        ppverifiedparker: string,
        contactid: number,
        phoneid: number,
        emailid: number,
        parkerid: number,
      |},
    }>
  | $Exact<{
      status: 404,
      reason: 'The user is already registered with us.',
      errorcode: 816,
    }>;

export const saveParker = ({ email, pin }: {| email: string, pin: string |}) =>
  api('saveparker', {
    query: { emailaddress: email, pincode: pin },
  });

export const login = ({ email, pin }: {| email: string, pin: string |}) =>
  api('login', {
    query: { email: email, pincode: pin, logintype: 'email-pin' },
  });

// A pending terms document, as returned on the `term` array of a login response.
// An empty array means there is nothing outstanding for this parker.
export type PendingTerm = {
  term_url?: string,
  condition_url?: string,
  term_version?: string,
};

// Records acceptance of the latest terms for this operator.
//
// `parkerid` MUST be passed explicitly. This endpoint authenticates as
// AUTH_TYPE_PARKER, which matches the request's parkerid against the
// ppsessiondata cookie. At the login step the ppverifiedparker cookie does not
// exist yet (only verifyemail2 sets it), so the server cannot resolve the
// parker on its own and the call would 403.
export const acceptLatestTerms = ({ parkerId }: {| parkerId: string |}) =>
  api('accepttermsandconditions', {
    query: { parkerid: parkerId },
  });

export type VerifyPinResponse =
  | $Exact<{
      type: 200,
      data: {|
        message: 'Valid login credentials',
        ppsessiondata: string,
        term: [],
      |},
    }>
  | $Exact<{
      status: 403,
      message: 'Unauthorized',
      authorizations: null,
      apiversion: null,
    }>;

export const verifyPin = ({ email, pin }: {| email: string, pin: string |}) =>
  api('verifypin', {
    query: { email, pincode: pin },
  });

export type UpdateAccountInfoResponse = $Exact<{
  status: 200,
  data: [],
  message: 'User information updated successfully.',
}>;

type UpdateAccountParams = {|
  firstname?: string,
  phonenumber?: string,
  paddress?: string,
  paddress2?: string,
  paddresscity?: string,
  paddresszip?: string,
  paddressstateabbreviation?: string,
  paddresscountry?: string,
  pincode?: string,
  pinresetbypassid?: number | null,
|};
export const updateAccountInfo = (params: UpdateAccountParams) =>
  api('updateaccountinfo', { query: params });

export type CreateFamilyAccountResponse = $Exact<{
  status: 200,
  data: {
    owned: Array<{
      id: string,
      name: string,
      owner_id: string,
      wallet_id: string,
      date_suspended: null,
      date_created: string,
      members: {
        active: Array<any>,
        removed: Array<any>,
        left: Array<any>,
      },
      invites: {
        pending: Array<any>,
        removed: Array<any>,
        expired: Array<any>,
      },
    }>,
    member: {
      active: Array<any>,
      removed: Array<any>,
      left: Array<any>,
      delete: Array<any>,
    },
  },
}>;

type CreateFamilyAccountParams = {|
  familyaccount_name: string,
  contact_email: string,
|};
export const createFamilyAccount = (params: CreateFamilyAccountParams) =>
  api('createfamilyaccount', { query: params });

type GrantPinResetBypassParams = {|
  member_id: number,
|};
export const grantPinResetBypass = (params: GrantPinResetBypassParams) =>
  api('grantpinresetbypass', { query: params });

export const beginPinReset = () => api('beginpinreset');



// WEBPACK FOOTER //
// ./src/api/auth.js